import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, TextField, Button, Divider,
  Switch, FormControlLabel, Alert, IconButton, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, MenuItem, Tabs, Tab, InputAdornment,
  useMediaQuery, useTheme
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, ContentCopy as CopyIcon,
  Star as StarIcon, StarBorder as StarBorderIcon, CloudUpload as UploadIcon,
  Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon,
  Download as DownloadIcon, Description as DescriptionIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import ExportTab from '../components/ExportTab';
import axios from 'axios';

function Settings() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user, isAdmin, isPartner, isManager } = useAuth();
  const isAdminRole = isAdmin();
  const isPartnerRole = isPartner();
  const isManagerRole = isManager ? isManager() : (user?.role === 'manager');
  
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  
  const [platformSettings, setPlatformSettings] = useState({
    company_name: '', company_email: '', company_phone: '',
    logo_url: '', admin_domain: '', default_postback_url: '',
    timezone: 'Europe/Rome', currency: 'EUR', date_format: 'DD/MM/YYYY'
  });
  
  const [trackingDomains, setTrackingDomains] = useState([]);
  const [newDomain, setNewDomain] = useState('');
  const [domainDialogOpen, setDomainDialogOpen] = useState(false);
  
  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [keyDialogOpen, setKeyDialogOpen] = useState(false);
  const [visibleKeys, setVisibleKeys] = useState({});
  
  const [notificationSettings, setNotificationSettings] = useState({
    notify_pending: true, notify_daily_cap: true,
    pending_threshold: 10, email_frequency: 'daily'
  });
  
  const [partnerPostback, setPartnerPostback] = useState('');
  
  const [passwordForm, setPasswordForm] = useState({
    current_password: '', new_password: '', confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState(false);

  useEffect(() => {
    if (isAdminRole) {
      fetchPlatformSettings();
      fetchTrackingDomains();
      fetchApiKeys();
    }
    if (isAdminRole || !isPartnerRole) {
      fetchNotificationSettings();
    }
    if (isPartnerRole) {
      fetchPartnerPostback();
    }
  }, [isAdminRole, isPartnerRole]);

  const fetchPlatformSettings = async () => {
    try {
      const res = await axios.get('/admin/platform-settings');
      setPlatformSettings(res.data.data);
    } catch (e) { console.error(e); }
  };

  const fetchTrackingDomains = async () => {
    try {
      const res = await axios.get('/admin/tracking-domains');
      setTrackingDomains(res.data.data);
    } catch (e) { console.error(e); }
  };

  const fetchApiKeys = async () => {
    try {
      const res = await axios.get('/admin/api-keys');
      setApiKeys(res.data.data);
    } catch (e) { console.error(e); }
  };

  const fetchNotificationSettings = async () => {
    try {
      const res = await axios.get('/admin/notification-settings');
      setNotificationSettings(res.data.data);
    } catch (e) { console.error(e); }
  };

  const fetchPartnerPostback = async () => {
    try {
      const res = await axios.get('/admin/my-postback');
      setPartnerPostback(res.data.postback_url || '');
    } catch (e) { console.error(e); }
  };

  const handleSavePlatformSettings = async () => {
    setLoading(true);
    try {
      await axios.put('/admin/platform-settings', platformSettings);
      setSuccess('Platform settings saved');
    } catch (e) { setError('Failed to save settings'); }
    setLoading(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const res = await axios.post('/admin/platform-settings/logo', { logo_data: event.target.result });
        setPlatformSettings({ ...platformSettings, logo_url: res.data.logo_url });
        setSuccess('Logo uploaded');
      } catch (e) { setError('Failed to upload logo'); }
    };
    reader.readAsDataURL(file);
  };

  const handleAddDomain = async () => {
    if (!newDomain) return;
    try {
      await axios.post('/admin/tracking-domains', { domain: newDomain, is_primary: false });
      fetchTrackingDomains();
      setNewDomain('');
      setDomainDialogOpen(false);
      setSuccess('Domain added');
    } catch (e) { setError('Failed to add domain'); }
  };

  const handleSetPrimaryDomain = async (id) => {
    try {
      await axios.put(`/admin/tracking-domains/${id}`, { is_primary: true });
      fetchTrackingDomains();
      setSuccess('Primary domain updated');
    } catch (e) { setError('Failed to update domain'); }
  };

  const handleToggleDomainActive = async (id, currentStatus) => {
    try {
      await axios.put(`/admin/tracking-domains/${id}`, { is_active: !currentStatus });
      fetchTrackingDomains();
    } catch (e) { setError('Failed to update domain'); }
  };

  const handleDeleteDomain = async (id) => {
    if (!window.confirm('Delete this domain?')) return;
    try {
      await axios.delete(`/admin/tracking-domains/${id}`);
      fetchTrackingDomains();
      setSuccess('Domain deleted');
    } catch (e) { setError(e.response?.data?.error || 'Failed to delete domain'); }
  };

  const handleGenerateApiKey = async () => {
    try {
      const res = await axios.post('/admin/api-keys', { key_name: newKeyName || 'API Key' });
      fetchApiKeys();
      setNewKeyName('');
      setKeyDialogOpen(false);
      setSuccess('API Key generated');
      setVisibleKeys({ ...visibleKeys, [res.data.data.id]: true });
    } catch (e) { setError('Failed to generate API key'); }
  };

  const handleDeleteApiKey = async (id) => {
    if (!window.confirm('Delete this API key?')) return;
    try {
      await axios.delete(`/admin/api-keys/${id}`);
      fetchApiKeys();
      setSuccess('API Key deleted');
    } catch (e) { setError('Failed to delete API key'); }
  };

  const handleToggleApiKey = async (id, currentStatus) => {
    try {
      await axios.put(`/admin/api-keys/${id}`, { is_active: !currentStatus });
      fetchApiKeys();
    } catch (e) { setError('Failed to update API key'); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard');
  };

  const handleSaveNotifications = async () => {
    setLoading(true);
    try {
      await axios.put('/admin/notification-settings', notificationSettings);
      setSuccess('Notification settings saved');
    } catch (e) { setError('Failed to save notification settings'); }
    setLoading(false);
  };

  const handleSavePartnerPostback = async () => {
    setLoading(true);
    try {
      await axios.put('/admin/my-postback', { postback_url: partnerPostback });
      setSuccess('Postback URL saved');
    } catch (e) { setError('Failed to save postback URL'); }
    setLoading(false);
  };

  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setError('Passwords do not match');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await axios.put('/admin/my-password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      setSuccess('Password changed successfully');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (e) { setError(e.response?.data?.error || 'Failed to change password'); }
    setLoading(false);
  };

  const handleDownloadGuide = async (guideType) => {
    try {
      const response = await axios.get(`/admin/guides/${guideType}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${guideType}_guide.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSuccess('Guide downloaded successfully');
    } catch (e) { setError('Failed to download guide'); }
  };

  const getTabs = () => {
    if (isAdminRole) return ['Account', 'Tracking', 'API Keys', 'Notifications', 'Global', 'Security', 'Guide', 'Export'];
    if (isManagerRole) return ['Notifications', 'Security', 'Guide'];
    if (isPartnerRole) return ['Postback', 'Security'];
    return ['Notifications', 'Security'];
  };

  const tabs = getTabs();

  // Guide documents list
  // Guide documents list
  const guides = [
    {
      id: 'postback',
      title: 'Postback System Guide',
      description: 'Complete guide for S2S postback integration. Includes supported parameters, status values, API responses, and partner postback configuration.',
      filename: 'Postback_Guide_Trackoo.pdf'
    },
    {
      id: 'facebook',
      title: 'Facebook Lead Ads Guide',
      description: 'Complete guide for Facebook Lead Ads integration. Includes dashboard analytics, campaign management, lead processing, webhook setup, and API integration.',
      filename: 'Facebook_Lead_Ads_Guide.pdf'
    },
    {
      id: 'dem',
      title: 'DEM Guide',
      description: 'Complete guide for Direct Email Marketing module. Includes calendar planning, database management, virtual lists configuration, and Maildome API integration.',
      filename: 'DEM_Guide.pdf'
    }
  ];

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={3} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>Settings</Typography>
      
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Card sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto" sx={{ minHeight: { xs: 40, sm: 48 } }}>
          {tabs.map((tab, i) => <Tab key={i} label={tab} sx={{ minHeight: { xs: 40, sm: 48 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }} />)}
        </Tabs>
      </Card>

      {/* ADMIN: Account Settings */}
      {isAdminRole && tabs[tabValue] === 'Account' && (
        <Card>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" fontWeight={600} mb={2} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Account Settings</Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Company Name" size={isMobile ? 'small' : 'medium'} value={platformSettings.company_name} onChange={(e) => setPlatformSettings({ ...platformSettings, company_name: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Email" size={isMobile ? 'small' : 'medium'} value={platformSettings.company_email} onChange={(e) => setPlatformSettings({ ...platformSettings, company_email: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Phone" size={isMobile ? 'small' : 'medium'} value={platformSettings.company_phone} onChange={(e) => setPlatformSettings({ ...platformSettings, company_phone: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Admin Domain" size={isMobile ? 'small' : 'medium'} value={platformSettings.admin_domain} onChange={(e) => setPlatformSettings({ ...platformSettings, admin_domain: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" mb={1}>Logo</Typography>
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                  {platformSettings.logo_url && <Box component="img" src={platformSettings.logo_url} sx={{ height: 50, maxWidth: 150, objectFit: 'contain' }} />}
                  <Button variant="outlined" component="label" startIcon={<UploadIcon />} size={isMobile ? 'small' : 'medium'}>
                    Upload Logo
                    <input type="file" hidden accept="image/*" onChange={handleLogoUpload} />
                  </Button>
                </Box>
              </Grid>
            </Grid>
            <Box mt={3}>
              <Button variant="contained" onClick={handleSavePlatformSettings} disabled={loading} size={isMobile ? 'small' : 'medium'}>Save Account Settings</Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ADMIN: Tracking Settings */}
      {isAdminRole && tabs[tabValue] === 'Tracking' && (
        <Card>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
              <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Tracking Domains</Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDomainDialogOpen(true)} size={isMobile ? 'small' : 'medium'}>Add Domain</Button>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <TableContainer>
              <Table size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell>Domain</TableCell>
                    {!isMobile && <TableCell>Primary</TableCell>}
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trackingDomains.map((domain) => (
                    <TableRow key={domain.id} hover>
                      <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{domain.domain}</TableCell>
                      {!isMobile && (
                        <TableCell>
                          <IconButton onClick={() => handleSetPrimaryDomain(domain.id)} color={domain.is_primary ? 'warning' : 'default'} size="small">
                            {domain.is_primary ? <StarIcon /> : <StarBorderIcon />}
                          </IconButton>
                        </TableCell>
                      )}
                      <TableCell>
                        <Chip label={domain.is_active ? 'Active' : 'Off'} color={domain.is_active ? 'success' : 'default'} size="small" onClick={() => handleToggleDomainActive(domain.id, domain.is_active)} sx={{ cursor: 'pointer', fontSize: { xs: '0.65rem', sm: '0.75rem' } }} />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => copyToClipboard(domain.domain)}><CopyIcon fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteDomain(domain.id)} disabled={domain.is_primary}><DeleteIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box mt={4}>
              <Typography variant="h6" fontWeight={600} mb={2} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Default Postback URL</Typography>
              <TextField fullWidth label="Default Postback URL" size={isMobile ? 'small' : 'medium'} value={platformSettings.default_postback_url} onChange={(e) => setPlatformSettings({ ...platformSettings, default_postback_url: e.target.value })} helperText="Default for new campaigns" />
              <Box mt={2}>
                <Button variant="contained" onClick={handleSavePlatformSettings} disabled={loading} size={isMobile ? 'small' : 'medium'}>Save Tracking Settings</Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ADMIN: API Keys */}
      {isAdminRole && tabs[tabValue] === 'API Keys' && (
        <Card>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
              <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>API Keys</Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setKeyDialogOpen(true)} size={isMobile ? 'small' : 'medium'}>Generate Key</Button>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <TableContainer>
              <Table size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    {!isMobile && <TableCell>API Key</TableCell>}
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {apiKeys.length === 0 ? (
                    <TableRow><TableCell colSpan={4} align="center"><Typography color="text.secondary">No API keys yet</Typography></TableCell></TableRow>
                  ) : apiKeys.map((key) => (
                    <TableRow key={key.id} hover>
                      <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{key.key_name}</TableCell>
                      {!isMobile && (
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{visibleKeys[key.id] ? key.api_key : '••••••••••••'}</Typography>
                            <IconButton size="small" onClick={() => setVisibleKeys({ ...visibleKeys, [key.id]: !visibleKeys[key.id] })}>{visibleKeys[key.id] ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}</IconButton>
                            <IconButton size="small" onClick={() => copyToClipboard(key.api_key)}><CopyIcon fontSize="small" /></IconButton>
                          </Box>
                        </TableCell>
                      )}
                      <TableCell>
                        <Chip label={key.is_active ? 'Active' : 'Off'} color={key.is_active ? 'success' : 'default'} size="small" onClick={() => handleToggleApiKey(key.id, key.is_active)} sx={{ cursor: 'pointer', fontSize: { xs: '0.65rem', sm: '0.75rem' } }} />
                      </TableCell>
                      <TableCell>
                        {isMobile && <IconButton size="small" onClick={() => copyToClipboard(key.api_key)}><CopyIcon fontSize="small" /></IconButton>}
                        <IconButton size="small" color="error" onClick={() => handleDeleteApiKey(key.id)}><DeleteIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Notifications */}
      {tabs[tabValue] === 'Notifications' && (
        <Card>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" fontWeight={600} mb={2} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Notification Settings</Typography>
            <Divider sx={{ mb: 3 }} />
            <Alert severity="info" sx={{ mb: 3 }}>Email notifications will be sent to your account email.</Alert>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel control={<Switch checked={notificationSettings.notify_pending} onChange={(e) => setNotificationSettings({ ...notificationSettings, notify_pending: e.target.checked })} />} label="Notify when conversions are pending approval" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth type="number" label="Pending Threshold" size={isMobile ? 'small' : 'medium'} value={notificationSettings.pending_threshold} onChange={(e) => setNotificationSettings({ ...notificationSettings, pending_threshold: parseInt(e.target.value) || 0 })} disabled={!notificationSettings.notify_pending} />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel control={<Switch checked={notificationSettings.notify_daily_cap} onChange={(e) => setNotificationSettings({ ...notificationSettings, notify_daily_cap: e.target.checked })} />} label="Notify when daily cap is reached" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth select label="Email Frequency" size={isMobile ? 'small' : 'medium'} value={notificationSettings.email_frequency} onChange={(e) => setNotificationSettings({ ...notificationSettings, email_frequency: e.target.value })}>
                  <MenuItem value="instant">Instant</MenuItem>
                  <MenuItem value="hourly">Hourly Digest</MenuItem>
                  <MenuItem value="daily">Daily Digest</MenuItem>
                </TextField>
              </Grid>
            </Grid>
            <Box mt={3}>
              <Button variant="contained" onClick={handleSaveNotifications} disabled={loading} size={isMobile ? 'small' : 'medium'}>Save Notification Settings</Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ADMIN: Global Settings */}
      {isAdminRole && tabs[tabValue] === 'Global' && (
        <Card>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" fontWeight={600} mb={2} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Global Configuration</Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField fullWidth select label="Timezone" size={isMobile ? 'small' : 'medium'} value={platformSettings.timezone} onChange={(e) => setPlatformSettings({ ...platformSettings, timezone: e.target.value })}>
                  <MenuItem value="Europe/Rome">Europe/Rome (CET)</MenuItem>
                  <MenuItem value="Europe/London">Europe/London (GMT)</MenuItem>
                  <MenuItem value="America/New_York">America/New_York (EST)</MenuItem>
                  <MenuItem value="UTC">UTC</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth select label="Currency" size={isMobile ? 'small' : 'medium'} value={platformSettings.currency} onChange={(e) => setPlatformSettings({ ...platformSettings, currency: e.target.value })}>
                  <MenuItem value="EUR">EUR (€)</MenuItem>
                  <MenuItem value="USD">USD ($)</MenuItem>
                  <MenuItem value="GBP">GBP (£)</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth select label="Date Format" size={isMobile ? 'small' : 'medium'} value={platformSettings.date_format} onChange={(e) => setPlatformSettings({ ...platformSettings, date_format: e.target.value })}>
                  <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
                  <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
                  <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
                </TextField>
              </Grid>
            </Grid>
            <Box mt={3}>
              <Button variant="contained" onClick={handleSavePlatformSettings} disabled={loading} size={isMobile ? 'small' : 'medium'}>Save Global Settings</Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* PARTNER: Postback */}
      {isPartnerRole && tabs[tabValue] === 'Postback' && (
        <Card>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" fontWeight={600} mb={2} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Your Postback URL</Typography>
            <Divider sx={{ mb: 3 }} />
            <Alert severity="info" sx={{ mb: 3 }}>Configure your postback URL to receive conversion notifications.</Alert>
            <TextField fullWidth label="Postback URL" size={isMobile ? 'small' : 'medium'} value={partnerPostback} onChange={(e) => setPartnerPostback(e.target.value)} helperText="Macros: {click_id}, {lead_id}, {payout}, {status}" />
            <Box mt={3}>
              <Button variant="contained" onClick={handleSavePartnerPostback} disabled={loading} size={isMobile ? 'small' : 'medium'}>Save Postback URL</Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Security */}
      {tabs[tabValue] === 'Security' && (
        <Card>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" fontWeight={600} mb={2} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Change Password</Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth type={showPasswords ? 'text' : 'password'} label="Current Password" size={isMobile ? 'small' : 'medium'} value={passwordForm.current_password} onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })} InputProps={{ endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowPasswords(!showPasswords)} size="small">{showPasswords ? <VisibilityOffIcon /> : <VisibilityIcon />}</IconButton></InputAdornment> }} />
              </Grid>
              <Grid item xs={12} md={6} />
              <Grid item xs={12} md={6}>
                <TextField fullWidth type={showPasswords ? 'text' : 'password'} label="New Password" size={isMobile ? 'small' : 'medium'} value={passwordForm.new_password} onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })} helperText="Minimum 6 characters" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth type={showPasswords ? 'text' : 'password'} label="Confirm New Password" size={isMobile ? 'small' : 'medium'} value={passwordForm.confirm_password} onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })} error={passwordForm.confirm_password && passwordForm.new_password !== passwordForm.confirm_password} helperText={passwordForm.confirm_password && passwordForm.new_password !== passwordForm.confirm_password ? 'Passwords do not match' : ''} />
              </Grid>
            </Grid>
            <Box mt={3}>
              <Button variant="contained" onClick={handleChangePassword} disabled={loading || !passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password} size={isMobile ? 'small' : 'medium'}>Change Password</Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ADMIN & MANAGER: Guide */}
      {(isAdminRole || isManagerRole) && tabs[tabValue] === 'Guide' && (
        <Card>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" fontWeight={600} mb={2} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Documentation & Guides</Typography>
            <Divider sx={{ mb: 3 }} />
            <Alert severity="info" sx={{ mb: 3 }}>Download the guides below to share with your clients and partners for integration setup.</Alert>
            
            <Grid container spacing={2}>
              {guides.map((guide) => (
                <Grid item xs={12} md={6} key={guide.id}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Box display="flex" alignItems="flex-start" gap={2}>
                        <DescriptionIcon sx={{ fontSize: 40, color: 'primary.main', mt: 0.5 }} />
                        <Box flex={1}>
                          <Typography variant="subtitle1" fontWeight={600}>{guide.title}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>{guide.description}</Typography>
                          <Button 
                            variant="contained" 
                            startIcon={<DownloadIcon />} 
                            onClick={() => handleDownloadGuide(guide.id)}
                            size={isMobile ? 'small' : 'medium'}
                          >
                            Download PDF
                          </Button>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {isAdminRole && tabs[tabValue] === 'Export' && (
        <ExportTab />
      )}

      {/* Dialogs */}
      <Dialog open={domainDialogOpen} onClose={() => setDomainDialogOpen(false)} fullScreen={isMobile} maxWidth="sm" fullWidth>
        <DialogTitle>Add Tracking Domain</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Domain" placeholder="track.example.com" value={newDomain} onChange={(e) => setNewDomain(e.target.value)} margin="normal" helperText="Enter without https://" />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDomainDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddDomain}>Add Domain</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={keyDialogOpen} onClose={() => setKeyDialogOpen(false)} fullScreen={isMobile} maxWidth="sm" fullWidth>
        <DialogTitle>Generate New API Key</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Key Name" placeholder="My Integration" value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)} margin="normal" />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setKeyDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleGenerateApiKey}>Generate Key</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Settings;
